// ============================================================================
// AVATAR KNOWLEDGE BASE SERVICE
// Phase 7: Avatar-specific knowledge bases
// ============================================================================

import { AvatarKBDocument } from '../types';
import { api } from './api';

/**
 * Upload document to avatar KB
 */
export async function uploadDocument(
  avatarDefinitionId: string,
  file: File,
  metadata?: { description?: string; tags?: string[] }
): Promise<{ success: boolean; document: AvatarKBDocument }> {
  // Normalize MIME type for .md files — browsers (especially macOS) often report empty type
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  const uploadFile =
    !file.type && extension === 'md'
      ? new File([file], file.name, { type: 'text/markdown' })
      : file;

  const formData = new FormData();
  formData.append('file', uploadFile);

  if (metadata) {
    formData.append('metadata', JSON.stringify(metadata));
  }

  const response = await api.post(
    `/api/avatar-kb/${avatarDefinitionId}/documents`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );

  return response.data;
}

/**
 * List avatar KB documents
 */
export async function listDocuments(
  avatarDefinitionId: string
): Promise<{ success: boolean; count: number; documents: AvatarKBDocument[] }> {
  const response = await api.get(
    `/api/avatar-kb/${avatarDefinitionId}/documents`
  );

  return response.data;
}

/**
 * Delete avatar KB document
 */
export async function deleteDocument(
  avatarDefinitionId: string,
  documentId: string
): Promise<{ success: boolean; message: string }> {
  const response = await api.delete(
    `/api/avatar-kb/${avatarDefinitionId}/documents/${documentId}`
  );

  return response.data;
}

/**
 * Query avatar KB
 */
export async function queryKB(
  avatarDefinitionId: string,
  query: string,
  topK?: number
): Promise<{ success: boolean; count: number; results: any[] }> {
  const response = await api.post(
    `/api/avatar-kb/${avatarDefinitionId}/query`,
    { query, topK }
  );

  return response.data;
}

/**
 * Search avatar KB with RAG
 */
export async function searchKB(
  avatarDefinitionId: string,
  query: string,
  options?: { topK?: number; includeAnswer?: boolean }
): Promise<{
  success: boolean;
  sources: Array<{
    documentId: string;
    fileName: string;
    content: string;
    relevanceScore: number;
    chunkIndex: number;
  }>;
  answer?: string;
}> {
  const response = await api.post(
    `/api/avatar-kb/${avatarDefinitionId}/search`,
    { query, ...options }
  );

  return response.data;
}

/**
 * Get avatar KB stats
 */
export async function getStats(
  avatarDefinitionId: string
): Promise<{
  success: boolean;
  totalDocuments: number;
  documentsProcessed: number;
  documentsProcessing: number;
  documentsPending: number;
  documentsFailed: number;
  totalChunks: number;
  totalSizeBytes: number;
}> {
  const response = await api.get(
    `/api/avatar-kb/${avatarDefinitionId}/stats`
  );

  return response.data;
}

/**
 * Test avatar KB quality
 */
export async function testKB(
  avatarDefinitionId: string
): Promise<{
  status: 'passed' | 'partial' | 'failed' | 'no_documents';
  message?: string;
  results: Array<{
    query: string;
    description: string;
    passed: boolean;
    latencyMs: number;
    sourcesFound: number;
    avgRelevance: number;
    answer: string | null;
    topSource: { fileName: string; relevanceScore: number; preview: string } | null;
    error?: string;
  }>;
  summary: {
    total: number;
    passed: number;
    failed: number;
    avgRelevance: number;
    avgLatencyMs: number;
    documentsIndexed: number;
  };
}> {
  const response = await api.post(
    `/api/avatar-kb/${avatarDefinitionId}/test`,
    {},
    { timeout: 120000 }
  );

  return response.data;
}

/**
 * Provision collection for avatar KB (Qdrant shared collection)
 */
export async function provisionCollection(
  avatarDefinitionId: string
): Promise<{ success: boolean; collectionName: string; endpoint: string; message: string }> {
  const response = await api.post(
    `/api/avatar-kb/${avatarDefinitionId}/provision`
  );

  return response.data;
}
